import { itemOptionsQuantitiesAtom } from "@/atoms";
import { Button, CheckIndicator, QuantityStepper } from "@/components/ui";
import { useAtom } from "jotai";
import type { MouseEvent, RefObject } from "react";

const MAX_QUANTITY = 99;

export default function OptionQuantityButton({
  buttonRef,
  maxPermitted,
  disabled,
  optionId,
  optionGroupId,
  allMaxPermittedOne,
}: {
  buttonRef: RefObject<HTMLButtonElement | null>;
  maxPermitted: number;
  disabled: boolean;
  optionId: string;
  optionGroupId: string;
  allMaxPermittedOne: boolean;
}) {
  const [itemOptionsQuantities, setItemOptionsQuantities] = useAtom(itemOptionsQuantitiesAtom);

  function applyQuantityDelta(delta: 1 | -1) {
    setItemOptionsQuantities((state) => {
      const currentGroupQty = state[optionGroupId] ?? {};
      const currentOptionQty = currentGroupQty[optionId] ?? 0;
      return {
        ...state,
        [optionGroupId]: {
          ...currentGroupQty,
          [optionId]: Math.max(currentOptionQty + delta, 0),
        },
      };
    });
  }

  function decreaseQuantity(event: MouseEvent) {
    event.stopPropagation();
    applyQuantityDelta(-1);
  }

  function increaseQuantity(event: MouseEvent) {
    event.stopPropagation();
    applyQuantityDelta(1);
  }

  const currentOptionQuantity = itemOptionsQuantities[optionGroupId]?.[optionId] ?? 0;
  const atCeiling =
    currentOptionQuantity > 0 &&
    (currentOptionQuantity === maxPermitted || currentOptionQuantity === MAX_QUANTITY);

  if (allMaxPermittedOne) {
    return (
      <CheckButton
        buttonRef={buttonRef}
        optionId={optionId}
        checked={currentOptionQuantity > 0}
        increaseQuantity={increaseQuantity}
        decreaseQuantity={decreaseQuantity}
      />
    );
  }

  return (
    <QuantityStepper
      variant="plain"
      size="compact"
      tone="primary"
      collapsible
      value={currentOptionQuantity}
      onIncrement={increaseQuantity}
      onDecrement={decreaseQuantity}
      incrementLabel="Aumentar quantidade da opção"
      decrementLabel="Diminuir quantidade da opção"
      incrementDisabled={disabled || atCeiling}
      incrementProps={{ "data-testid": `option-qty-increase-${optionId}` }}
      decrementProps={{ "data-testid": `option-qty-decrease-${optionId}` }}
      valueProps={{ "data-testid": `option-qty-${optionId}` }}
    />
  );
}

function CheckButton({
  buttonRef,
  optionId,
  checked,
  increaseQuantity,
  decreaseQuantity,
}: {
  buttonRef: RefObject<HTMLButtonElement | null>;
  optionId: string;
  checked: boolean;
  increaseQuantity: (event: MouseEvent) => void;
  decreaseQuantity: (event: MouseEvent) => void;
}) {
  return (
    <Button
      ref={buttonRef as RefObject<HTMLButtonElement>}
      variant="plain"
      tone="neutral"
      size="icon-md"
      shape="circle"
      role="checkbox"
      aria-checked={checked}
      data-testid={`option-check-${optionId}`}
      onClick={checked ? decreaseQuantity : increaseQuantity}
    >
      <CheckIndicator checked={checked} tone="secondary" size="sm" />
    </Button>
  );
}
